import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import './BreakdownMain.css';
import { useScenesState } from './Hooks/UseScenesState';
import { useSceneCRUD } from './Hooks/UseSceneCRUD';
import { useSceneUpdates } from './Hooks/UseSceneUpdates';
import { useSearchHandlers } from './Hooks/UseSearchHandlers';
import { useBreakdownData } from './Hooks/UseBreakdownData';
import { useDataCache } from '../../../contexts/BreakdownDataCacheContext';
import { useTabCache } from '../../../contexts/TabCacheContext';
import { useUserPermissions } from '../../../contexts/UserPermissionsContext';
import { GranteeType, PermissionRole, ResourceType } from '../../../api/userPermissions';
import { getCurrentUserID } from '../../../utils/userUtils';
import { useBreakdownTabContext } from '../../../contexts/BreakdownTabContext';
import { useCurrentUser } from '../../../contexts/CurrentUserContext';
import { useFilmContext } from '../../../contexts/FilmContext';
// 旧的快捷键系统已移除，现在使用 ExcelTable 自己的快捷键系统
import ConfirmDialog, { useConfirmDialog } from '../../../components/UI/ConfirmDialog';

import { useOutletContext, useParams, useLocation } from 'react-router-dom';
import ScenesTable from '../ScenesTable/ScenesTable';
import { SceneWithUserNames } from '../../../types/scene.types';
import { ElementWithUserNames } from '../../../types/element.types';
import SceneDetails from '../SceneDetails/SceneDetails';
import ElementSearch from '../../../components/ElementSearch/ElementSearch';
import { 
  calculateSceneOrder, 
  calculateTotalPages, 
  calculatePagesUpToScene 
} from './Utils/sceneCalculations';
import {
  LayoutContext
} from './BreakdownMain.types';
// 统一导航系统
import { useUnifiedNavigationState, UnifiedPosition } from '../shortcut/useUnifiedNavigationState';
import { useUnifiedKeyboardNavigation } from '../shortcut/useUnifiedKeyboardNavigation';
import { useUnifiedMouseInteraction } from '../shortcut/useUnifiedMouseInteraction';
export type { SceneCodeParts } from './Utils/SceneCodeUtils';

interface BreakdownMainProps {
  breakdownId?: string; // 可选，如果提供则使用，否则从 URL params 获取
}

const BreakdownMain: React.FC<BreakdownMainProps> = ({ breakdownId: breakdownIdProp }) => {
  const { breakdownId: breakdownIdFromParams } = useParams<{ breakdownId: string }>();
  const { breakdownId: contextBreakdownId } = useBreakdownTabContext();
  // 优先使用 context，确保和 TabBar 使用相同的 breakdownId
  const breakdownId = contextBreakdownId || breakdownIdProp || breakdownIdFromParams;
  const { t } = useTranslation();
  const location = useLocation();
  const { tabs } = useOutletContext<LayoutContext>();
  const { clearBreakdownCache, getBreakdownCache } = useDataCache();
  const { setCache: setTabCache, clearCache: clearTabCache } = useTabCache();
  const { clearPermissions, getPermissions } = useUserPermissions();
  const { setLocalOnlyField, setServerSyncedField } = useCurrentUser();
  const { reloadCurrentFilmChildren } = useFilmContext();
  const homeBreakdownCardRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const scheduleHomeBreakdownMetadataRefresh = useCallback(() => {
    if (homeBreakdownCardRefreshTimerRef.current !== undefined) {
      clearTimeout(homeBreakdownCardRefreshTimerRef.current);
    }
    homeBreakdownCardRefreshTimerRef.current = setTimeout(() => {
      homeBreakdownCardRefreshTimerRef.current = undefined;
      void reloadCurrentFilmChildren();
    }, 600);
  }, [reloadCurrentFilmChildren]);

  // 获取当前标签页ID
  const currentTab = tabs.find(tab => tab.path === location.pathname);
  const currentTabId = currentTab?.id || `breakdownList-${breakdownId}`;
  
  // 统一管理场景状态
  const {
    // 基础状态
    breakdownInfo,
    setBreakdownInfo,
    scenes,
    setScenes,
    loading,
    setLoading,
    error,
    setError,
    selectedSceneId,
    setSelectedSceneId,
    selectedSceneData,
    setSelectedSceneData,
    breakdownName,
    setBreakdownName,
    originalBreakdownName,
    setOriginalBreakdownName,
    sortConfig,
    setSortConfig,
    // 本分场拍摄计划列表（与 TabCache schedulesList 同步）
    schedulesList,
    setSchedulesList,
    recentlyUpdatedScheduleId,
    setRecentlyUpdatedScheduleId,
    // SetSearch相关状态
    setSearchVisible,
    setSetSearchVisible,
    setSearchText,
    setSetSearchText,
    activeSceneId,
    setActiveSceneId,
    setSetSearchPosition,
    setLocalSceneSetDisplay,
    setLocalSceneLocationDisplay,
    // 地点搜索相关状态
    sceneLocationSearchVisible,
    setSceneLocationSearchVisible,
    sceneLocationSearchText,
    setSceneLocationSearchText,
    activeLocationSceneId,
    setActiveLocationSceneId,
    setSceneLocationSearchPosition,
    sceneLocationDisplayTexts,
    setSceneLocationDisplayTexts,
    sceneSetDisplayTexts,
    setSceneSetDisplayTexts,
    setSceneLocationPreviewText,
    setSetPreviewText,
    // ElementSearch相关状态
    elementSearchVisible,
    setElementSearchVisible,
    elementSearchType,
    setElementSearchType,
    // Scene组件刷新触发器
    sceneRefreshTrigger,
    setSceneRefreshTrigger,
    // 已选择的场次元素ID列表
    selectedElementIds,
    setSelectedElementIds,
    // Refs
    pendingSceneUpdatesRef,
    updateTimersRef,
    sceneRef,
    lastSelectedSceneIdByBreakdownIdRef,
  } = useScenesState();

  const currentUserID = getCurrentUserID();
  const currentBreakdownRole = breakdownId
    ? (getPermissions(ResourceType.BREAKDOWN, breakdownId) || []).find(
        (permission) =>
          permission.granteeType === GranteeType.USER && permission.granteeID === currentUserID
      )?.role
    : undefined;
  const canEditBreakdown = Boolean(
    breakdownInfo?.breakdownOwnerID === currentUserID ||
    currentBreakdownRole === PermissionRole.ADMIN ||
    currentBreakdownRole === PermissionRole.EDITOR
  );

  // 确认对话框 Hook
  const { dialogState, showConfirm, handleConfirm, handleCancel } = useConfirmDialog();

  // ==================== 统一导航系统初始化 ====================
  // 统一导航状态管理
  const {
    currentStateAndPosition,
    getPositionState,
    setSelection,
    setEditing,
    setHover,
  } = useUnifiedNavigationState();

  useEffect(() => {
    if (!canEditBreakdown) setEditing(null);
  }, [canEditBreakdown, setEditing]);

  // 元素引用映射（使用 useRef 存储，避免不必要的重渲染）
  const scenesTableElementRefs = useRef<Map<string, HTMLElement>>(new Map());
  const sceneDetailsElementRefs = useRef<Map<string, HTMLElement>>(new Map());
  const elementTableElementRefs = useRef<Map<string, HTMLElement>>(new Map());

  // 获取当前选中场景的场次元素列表（用于导航地图构建）
  const [selectedElements, setSelectedElements] = useState<ElementWithUserNames[]>([]);

  // 构建 columns 数组（用于 getPositionFromElement）
  const columns = useMemo(() => [
    { key: 'sceneCode' },
    { key: 'sceneInteriorExterior' },
    { key: 'sceneDayNight' },
    { key: 'sceneSet' },
    { key: 'scenePlotDay' },
    { key: 'scenePagesNumber' },
    { key: 'scenePlot' },
    { key: 'sceneLocation' },
    { key: 'scenePeriod' },
  ], []);

  // 使用 ref 存储最新的 breakdown，避免闭包问题
  const scenesRef = useRef<SceneWithUserNames[]>(scenes);
  useEffect(() => {
    scenesRef.current = scenes;
  }, [scenes]);

  // 从 DOM 元素获取 UnifiedPosition
  const getPositionFromElement = useCallback((element: HTMLElement): UnifiedPosition | null => {
    // BreakdownTable 元素：检查 data-row-id 和 data-col-key
    const sceneID = element.getAttribute('data-row-id') || 
                   element.closest('[data-row-id]')?.getAttribute('data-row-id');
    const colKey = element.getAttribute('data-col-key') || 
                   element.closest('[data-col-key]')?.getAttribute('data-col-key');
    
    if (sceneID && colKey) {
      // 从 scenes 数组找到对应的行索引（ref 避免闭包滞后）
      const rowIndex = scenesRef.current.findIndex(s => s.sceneID === sceneID);
      if (rowIndex === -1) return null;
      
      // 从 columns 数组找到对应的列索引
      const colIndex = columns.findIndex(col => col.key === colKey);
      if (colIndex === -1) return null;
      
      return {
        type: 'scenesTable',
        sceneID,
        row: rowIndex,
        col: colIndex,
        colKey,
      };
    }

    // SceneDetails 元素：检查 data-field
    const field = element.getAttribute('data-field') || 
                  element.closest('[data-field]')?.getAttribute('data-field');
    if (field && selectedSceneId) {
      // 检查是否是 SceneDetails 的字段
      const sceneDetailsFields = [
        'sceneInteriorExterior',
        'sceneDayNight',
        'sceneSet',
        'scenePlotDay',
        'scenePagesNumber',
        'scenePlot',
        'sceneLocation',
        'scenePlotSummary',
      ];
      if (sceneDetailsFields.includes(field)) {
        return {
          type: 'sceneDetails',
          sceneID: selectedSceneId,
          field,
        };
      }
    }

    // ElementTable 元素：检查 data-item-id 和 data-field
    const elementID = element.getAttribute('data-item-id') || 
                        element.closest('[data-item-id]')?.getAttribute('data-item-id');
    const itemField = element.getAttribute('data-field');
    
    if (elementID && itemField && (itemField === 'name' || itemField === 'quantity')) {
      return {
        type: 'elementTable',
        elementID,
        field: itemField as 'name' | 'quantity',
      };
    }

    return null;
  }, [columns, selectedSceneId]);

  // 激活新创建行的场号输入框编辑状态（使用统一导航系统）
  const activateNewSceneCodeEdit = useCallback((sceneId: string) => {
    // 与编辑状态保持一致：新建行进入编辑前先切换当前选中行
    setSelectedSceneId(sceneId);
    setSelectedSceneData(undefined);

    // 场号列是第0列
    const colIndex = 0;
    const colKey = 'sceneCode';
    
    // 查找行索引的函数（优先从 DOM 查找，后备从 breakdown 数组查找）
    const findRowIndex = (): number => {
      // 方法1：从 DOM 查找（最可靠，因为 DOM 已经渲染）
      // 查找包含该 sceneId 的单元格，然后找到其所在的行
      const cell = document.querySelector(`td[data-row-id="${sceneId}"]`) as HTMLTableCellElement;
      if (cell) {
        const tableRow = cell.closest('tr') as HTMLTableRowElement;
        if (tableRow) {
          const tbody = tableRow.closest('tbody');
          if (tbody) {
            const rows = Array.from(tbody.querySelectorAll('tr'));
            return rows.indexOf(tableRow);
          }
        }
      }
      
      // 方法2：从 breakdown 数组查找（后备方案）
      const latestScenes = scenesRef.current;
      return latestScenes.findIndex(s => s.sceneID === sceneId);
    };
    
    // 尝试查找行索引
    let rowIndex = findRowIndex();
    
    // 如果找不到，等待 DOM 更新后再试
    if (rowIndex === -1) {
      setTimeout(() => {
        rowIndex = findRowIndex();
        if (rowIndex === -1) return;
        
        const position: UnifiedPosition = {
          type: 'scenesTable',
          sceneID: sceneId,
          row: rowIndex,
          col: colIndex,
          colKey,
        };
        
        setEditing(position);
        
        // 等待渲染完成后再聚焦
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setTimeout(() => {
              const key = `${rowIndex}-${colIndex}`;
              const element = scenesTableElementRefs.current.get(key);
              if (element) {
                if (element.tagName === 'TD') {
                  const input = element.querySelector('input') as HTMLInputElement;
                  if (input) {
                    input.focus();
                    input.select();
                  }
                } else if (element.tagName === 'INPUT') {
                  const input = element as HTMLInputElement;
                  input.focus();
                  input.select();
                }
              }
            }, 0);
          });
        });
      }, 100);
      return;
    }
    
    const position: UnifiedPosition = {
      type: 'scenesTable',
      sceneID: sceneId,
      row: rowIndex,
      col: colIndex,
      colKey,
    };
    
    // 使用统一导航系统激活编辑状态
    setEditing(position);
    
    // 等待渲染完成后再聚焦
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(() => {
          const key = `${rowIndex}-${colIndex}`;
          const element = scenesTableElementRefs.current.get(key);
          if (element) {
            // 如果元素是 td，查找内部的输入框
            if (element.tagName === 'TD') {
              const input = element.querySelector('input') as HTMLInputElement;
              if (input) {
                input.focus();
                input.select();
              }
            } else if (element.tagName === 'INPUT') {
              const input = element as HTMLInputElement;
              input.focus();
              input.select();
            }
          }
        }, 0);
      });
    });
  }, [setEditing, scenesTableElementRefs, setSelectedSceneId, setSelectedSceneData]);

  // CRUD 操作 Hook
  const {
    handleAddSceneInTable,
    handleAddSceneInTableEnd,
    handleDeleteScene,
    handleCopyScene,
    handleSceneDetailUpdate,
    createSceneData,
  } = useSceneCRUD(
    breakdownId,
    scenes,
    setScenes,
    selectedSceneId,
    setSelectedSceneId,
    setSelectedSceneData,
    sortConfig,
    setSceneLocationDisplayTexts,
    activateNewSceneCodeEdit, // 传递回调函数，用于激活新创建行的场号输入框编辑状态
    scheduleHomeBreakdownMetadataRefresh,
  );

  // 初始化统一键盘导航
  useUnifiedKeyboardNavigation({
    enabled: canEditBreakdown,
    scenes,
    selectedSceneId,
    elements: selectedElements,
    columns,
    currentStateAndPosition,
    setSelection,
    setEditing,
    setSelectedSceneId,
    scenesTableElementRefs: scenesTableElementRefs.current,
    sceneDetailsElementRefs: sceneDetailsElementRefs.current,
    elementTableElementRefs: elementTableElementRefs.current,
    getPositionFromElement,
  });

  // 初始化统一鼠标交互
  useUnifiedMouseInteraction({
    enabled: canEditBreakdown,
    setSelection,
    setEditing,
    setHover,
    currentStateAndPosition,
    scenesTableElementRefs: scenesTableElementRefs.current,
    sceneDetailsElementRefs: sceneDetailsElementRefs.current,
    elementTableElementRefs: elementTableElementRefs.current,
    getPositionFromElement,
  });

  // 同步 currentStateAndPosition 到 CurrentUserContext（localOnly）
  useEffect(() => {
    if (breakdownId) {
      setLocalOnlyField('userLastUsedBreakdownCurrentStateAndPosition', currentStateAndPosition);
    }
  }, [currentStateAndPosition, breakdownId, setLocalOnlyField]);

  // 注意：场景切换时不再清除状态
  // 因为切换场景时，用户会通过点击或键盘导航进入编辑或选中状态
  // 清除状态会导致用户体验不佳
  // 状态会在用户明确操作（如点击其他区域、按 ESC 等）时自然清除

  // 注意：旧的状态清除系统已移除，现在使用统一导航系统的 clearAllStates

  // 包装删除函数，显示确认对话框
  const handleDeleteSceneWithConfirm = useCallback((sceneId: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      // 找到要删除的场次信息
      const sceneToDelete = scenes.find(scene => scene.sceneID === sceneId);
      const sceneCode = sceneToDelete?.sceneCode;
      
      const confirmMessage = sceneCode 
        ? `确定要删除场次 [ ${sceneCode} ] 吗？`
        : '确定要删除这个场次吗？';
      
      showConfirm({
        message: confirmMessage,
        confirmText: '确认',
        cancelText: '取消',
        onConfirm: async () => {
          try {
            await handleDeleteScene(sceneId);
            resolve();
          } catch (error) {
            reject(error);
          }
        },
        onCancel: () => {
          resolve(); // 取消时也 resolve，不 reject
        }
      });
    });
  }, [scenes, showConfirm, handleDeleteScene]);
  
  // 场次更新逻辑 Hook（防抖）
  const {
    handleSceneUpdate,
    flushSceneUpdate,
  } = useSceneUpdates(
    scenes,
    selectedSceneId,
    setScenes,
    setSelectedSceneData,
    pendingSceneUpdatesRef,
    updateTimersRef,
    createSceneData, // 传入 createSceneData，用于占位行创建真实数据
    setSelectedSceneId, // 传入 setSelectedSceneId，用于更新选中状态
    scheduleHomeBreakdownMetadataRefresh,
  );
  
  // 注意：旧的快捷键系统已移除，现在使用 ExcelTable 自己的快捷键系统
  // useEnterKeyHandler();
  // useTabKeyHandler();
  // useArrowKeyHandler();
  
  // 监听方向键导航时的行选择更新事件
  useEffect(() => {
    const handleArrowKeyRowChange = (e: Event) => {
      const customEvent = e as CustomEvent<{ sceneId: string }>;
      const { sceneId } = customEvent.detail;
      if (sceneId) {
        setSelectedSceneId(sceneId);
        setSelectedSceneData(undefined);
      }
    };

    // 在表格容器上监听事件
    const tableContainer = document.querySelector('.breakdown-table-container');
    if (tableContainer) {
      tableContainer.addEventListener('arrow-key-row-change', handleArrowKeyRowChange as EventListener);
      return () => {
        tableContainer.removeEventListener('arrow-key-row-change', handleArrowKeyRowChange as EventListener);
      };
    }
  }, [setSelectedSceneId, setSelectedSceneData]);
  
  // 数据获取和初始化 Hook
  useBreakdownData(
    breakdownId,
    sortConfig,
    selectedSceneId,
    sceneRefreshTrigger,
    setBreakdownInfo,
    setScenes,
    setLoading,
    setError,
    setBreakdownName,
    setOriginalBreakdownName,
    setSceneLocationDisplayTexts,
    setSceneSetDisplayTexts,
    setSelectedSceneId,
    setSelectedElementIds,
    lastSelectedSceneIdByBreakdownIdRef,
    setSortConfig,
    setSchedulesList,
    setRecentlyUpdatedScheduleId
  );

  // 注意：registerBreakdownStateUpdaters 已移除，TabBar 的查找替换现在通过 BreakdownDataCacheContext 订阅更新

  // 只同步 UI 状态到 TabCache（不更新 BreakdownDataCacheContext，因为数据缓存应该由更新 hooks 负责）
  // breakdown 和 breakdownInfo 应该只从缓存读取，不应该反向同步到缓存
  useEffect(() => {
    if (!breakdownId || !currentTabId || !location.pathname) return;
    
    // 只保存 UI 状态到 TabCache，不更新 BreakdownDataCacheContext
    const tabCacheData = {
      sceneLocationDisplayTexts,
      selectedSceneId,
      sortConfig,
      breakdownName,
      originalBreakdownName,
      schedulesList,
      recentlyUpdatedScheduleId,
    };
    setTabCache(currentTabId, breakdownId, location.pathname, tabCacheData);
  }, [
    breakdownId,
    currentTabId,
    location.pathname,
    setTabCache,
    sceneLocationDisplayTexts,
    selectedSceneId,
    sortConfig,
    breakdownName,
    originalBreakdownName,
    schedulesList,
    recentlyUpdatedScheduleId,
  ]);

  // 当用户选中场次时：内存 ref + 服务端同步 userLastUsedSceneID（`breakdownID;sceneID` 数组，按分场去重）
  useEffect(() => {
    if (!breakdownId || !selectedSceneId) return;
    const inList = scenes.some((s) => s.sceneID === selectedSceneId);
    if (!inList) return;
    lastSelectedSceneIdByBreakdownIdRef.current.set(breakdownId, selectedSceneId);
    setServerSyncedField('userLastUsedSceneID', `${breakdownId};${selectedSceneId}`);
  }, [breakdownId, selectedSceneId, scenes, setServerSyncedField, lastSelectedSceneIdByBreakdownIdRef]);
  
  // 移除自动同步逻辑：不再监听 breakdown 变化自动更新 selectedSceneData
  // selectedSceneData 应该只通过缓存订阅者更新，或者通过用户选择行时更新

  // 托底机制：如果数据为空且不在加载中，且没有错误，则清除缓存触发重新加载
  // 使用 ref 防止无限循环
  const reloadTriggerRef = useRef<{ breakdownId: string | null; timestamp: number }>({ breakdownId: null, timestamp: 0 });
  
  useEffect(() => {
    if (!breakdownId || loading || error) return;
    
    // 检查数据是否为空（没有 breakdownInfo）
    // 注意：允许 breakdown 为空数组，只要 breakdownInfo 存在即可
    const isDataEmpty = !breakdownInfo;
    
    if (isDataEmpty) {
      // 检查缓存是否存在，如果缓存存在（即使 breakdownInfo 为空），说明 BreakdownContainer 正在加载中
      // 不应该清除缓存，避免干扰 BreakdownContainer 的加载过程
      const breakdownCache = getBreakdownCache(breakdownId);
      if (breakdownCache) {
        // 缓存存在，检查是否有 breakdown 数据或缓存是最近创建的（1秒内）
        // 如果有 breakdown 数据或缓存是最近创建的，说明正在加载中，跳过清除缓存
        const hasScenesData = breakdownCache.scenes && Array.isArray(breakdownCache.scenes) && breakdownCache.scenes.length > 0;
        const isRecentCache = breakdownCache.timestamp && (Date.now() - breakdownCache.timestamp < 1000);
        if (hasScenesData || isRecentCache) {
          // 缓存存在且有数据，或缓存是最近创建的，说明正在加载中，跳过清除缓存
          return;
        }
      }
      
      // 防止无限循环：如果最近已经触发过重新加载（5秒内），则不再触发
      const now = Date.now();
      const lastReload = reloadTriggerRef.current;
      if (lastReload.breakdownId === breakdownId && now - lastReload.timestamp < 5000) {
        return;
      }
      // 记录重新加载时间
      reloadTriggerRef.current = { breakdownId, timestamp: now };
      
      // 清除缓存，触发 useBreakdownData 重新加载
      const tabId = `breakdownList-${breakdownId}`;
      // 在清除缓存前先获取场次数据，用于清除权限
      const breakdownCacheBeforeClear = getBreakdownCache(breakdownId);
      clearBreakdownCache(breakdownId);
      clearTabCache(tabId);
      // 清除相关权限缓存
      clearPermissions(ResourceType.BREAKDOWN, breakdownId);
      // 清除该分场下所有场次的权限（如果缓存中有场次数据）
      if (breakdownCacheBeforeClear?.scenes) {
        breakdownCacheBeforeClear.scenes.forEach((scene) => {
          clearPermissions(ResourceType.SCENE, scene.sceneID);
        });
      }
      // 由于我们已经清除了缓存，useBreakdownData 会在下次检查时发现缓存无效，从而重新加载
    }
  }, [breakdownId, breakdownInfo, scenes, loading, error, clearBreakdownCache, clearTabCache, getBreakdownCache, clearPermissions]);

  // 场次元素列表数据（从Scene组件获取）
  const [, setElements] = useState<ElementWithUserNames[]>([]);

  // 关闭场次详情
  const handleCloseSceneDetail = () => {
    setSelectedSceneId(null);
    setSelectedSceneData(undefined); // 重置外部数据状态
  };





  // 键盘事件处理已移除，等待重新设计快捷键功能组件

  // 创建一个 finishCellEdit 的包装函数，用于传递给 useSearchHandlers
  // 注意：实际的 finishCellEdit 在 BreakdownTable 中，这里创建一个占位函数
  // 如果需要，可以通过 ref 或 callback 从 BreakdownTable 获取真实的 finishCellEdit
  const finishCellEditWrapper = useCallback((rowIndex: number, columnKey: string, skipUpdate?: boolean) => {
    // 这个函数现在只是一个占位，实际的实现应该在 BreakdownTable 中
    // 如果需要，可以通过 ref 或 callback 从 BreakdownTable 获取
    console.warn('finishCellEdit called from useSearchHandlers, but actual implementation is in BreakdownTable');
  }, []);
  
  // 搜索处理 Hook
  const {
    handleElementSearchChange,
    handleElementSearchClose,
  } = useSearchHandlers(
    scenes,
    breakdownId,
    activeSceneId,
    setActiveSceneId,
    setSearchVisible,
    setSetSearchVisible,
    setSearchText,
    setSetSearchText,
    setSetSearchPosition,
    setSetPreviewText,
    setLocalSceneSetDisplay,
    activeLocationSceneId,
    setActiveLocationSceneId,
    sceneLocationSearchVisible,
    setSceneLocationSearchVisible,
    sceneLocationSearchText,
    setSceneLocationSearchText,
    setSceneLocationSearchPosition,
    setSceneLocationPreviewText,
    setLocalSceneLocationDisplay,
    setSceneLocationDisplayTexts,
    setElementSearchVisible,
    setElementSearchType,
    selectedSceneId,
    setSelectedElementIds,
    sceneRef,
    handleSceneUpdate,
    finishCellEditWrapper
  );

  if (!breakdownId) {
    return (
      <div className="breakdown-container">
        <div className="error-message">{t('breakdown.invalidBreakdownId')}</div>
      </div>
    );
  }

  return (
    <div className="breakdown-container">
      {/* Header 区域 
      <div className="breakdown-header">
        <div className="breakdown-header-left">
          <div className="breakdown-title-container">
            <input
              type="text"
              placeholder="未命名分场"
              value={breakdownName}
              onChange={(e) => setBreakdownName(e.target.value)}
              onBlur={handleBreakdownNameBlur}
              className="breakdown-title-input"
            />
          </div>
          

          分场信息区域（暂时隐藏）
          <div className="breakdown-info">
            <span>{t('breakdown.status')}{t('common.labelSeparator')}{breakdownInfo?.breakdownStatus}</span>
            <span>{t('scenes.totalRows')}{t('common.labelSeparator')}{scenes.length}</span>
            <span>{t('breakdown.totalPlotDays')}{t('common.labelSeparator')}{calculateTotalPlotDays()}</span>
            <span>{t('breakdown.totalPages')}{t('common.labelSeparator')}{calculateTotalPages(scenes)}</span>
          </div>


        </div>
        <div className="breakdown-header-right">
        </div>
      </div>
      */}

      {/* 表格区域 */}
      <div className="breakdown-content">
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <span>{t('common.loading')}</span>
          </div>
        ) : error ? (
          <div className="error-container">
            <div className="error-message">
              <span>❌ {error}</span>
              <button className="btn-retry" onClick={() => window.location.reload()}>{t('common.retry')}</button>
            </div>
          </div>
        ) : (
          <div className="breakdown-split-container">
            {/* 左侧：场次列表表格 */}
            <div 
              className="breakdown-table-wrapper"
              style={{ position: 'relative' }}
            >
              <ScenesTable
                scenes={scenes}
                breakdownInfo={breakdownInfo}
                selectedSceneId={selectedSceneId}
                setSelectedSceneId={setSelectedSceneId}
                setSelectedSceneData={setSelectedSceneData}
                sortConfig={sortConfig}
                setSortConfig={setSortConfig}
                sceneLocationDisplayTexts={sceneLocationDisplayTexts}
                setSceneLocationDisplayTexts={setSceneLocationDisplayTexts}
                sceneSetDisplayTexts={sceneSetDisplayTexts}
                setSceneSetDisplayTexts={setSceneSetDisplayTexts}
                setSearchVisible={setSearchVisible}
                setSetSearchVisible={setSetSearchVisible}
                setSearchText={setSearchText}
                setSetSearchText={setSetSearchText}
                activeSceneId={activeSceneId}
                setActiveSceneId={setActiveSceneId}
                setSetSearchPosition={setSetSearchPosition}
                setLocalSceneSetDisplay={setLocalSceneSetDisplay}
                setLocalSceneLocationDisplay={setLocalSceneLocationDisplay}
                sceneLocationSearchVisible={sceneLocationSearchVisible}
                setSceneLocationSearchVisible={setSceneLocationSearchVisible}
                sceneLocationSearchText={sceneLocationSearchText}
                setSceneLocationSearchText={setSceneLocationSearchText}
                activeLocationSceneId={activeLocationSceneId}
                setActiveLocationSceneId={setActiveLocationSceneId}
                setSceneLocationSearchPosition={setSceneLocationSearchPosition}
                handleSceneUpdate={handleSceneUpdate}
                flushSceneUpdate={flushSceneUpdate}
                handleAddSceneInTable={handleAddSceneInTable}
                handleAddSceneInTableEnd={handleAddSceneInTableEnd}
                handleDeleteScene={handleDeleteScene}
                breakdownId={breakdownId}
                canEdit={canEditBreakdown}
                setScenes={setScenes}
                // 注意：onClearOtherStates 和 onRegisterClearState 已废弃，统一导航系统会自动处理状态互斥
                // 统一导航系统
                currentStateAndPosition={currentStateAndPosition}
                getPositionState={getPositionState}
                setSelection={setSelection}
                scenesTableElementRefs={scenesTableElementRefs}
              />

            </div>
            {/* 右侧：场次详情 */}
            <div className="scene-detail-container">
              {selectedSceneId ? (
                <SceneDetails
                  key={`${breakdownId}-${selectedSceneId}`}
                  ref={sceneRef}
                  sceneId={selectedSceneId}
                  visible={true}
                  onClose={handleCloseSceneDetail}
                  onSceneUpdate={handleSceneDetailUpdate}
                  externalSceneData={selectedSceneData}
                  refreshTrigger={sceneRefreshTrigger}
                  onCopyScene={handleCopyScene}
                  onDeleteScene={handleDeleteSceneWithConfirm}
                  onElementRemoved={() => {
                    // 当场次元素被移除时，刷新已选择元素列表
                    setSceneRefreshTrigger(prev => prev + 1);
                  }}
                  sceneOrder={calculateSceneOrder(scenes, selectedSceneId)}
                  totalScenes={scenes.length}
                  pagesUpToScene={calculatePagesUpToScene(scenes, selectedSceneId)}
                  totalPages={calculateTotalPages(scenes)}
                  onElementsChange={setElements}
                  allScenes={scenes as any}
                  // 注意：旧的状态清除 props 已废弃，统一导航系统会自动处理状态互斥
                  // 统一导航系统
                  currentStateAndPosition={currentStateAndPosition}
                  getPositionState={getPositionState}
                  sceneDetailsElementRefs={sceneDetailsElementRefs}
                  elementTableElementRefs={elementTableElementRefs}
                  onElementsChangeForNav={setSelectedElements}
                  canEdit={canEditBreakdown}
                />
              ) : (
                <div className="scene-detail-empty">
                  <div className="scene-detail-empty-content">
                    <div className="scene-detail-empty-icon">📝</div>
                    <div className="scene-detail-empty-title">请选择一个场次</div>
                    <div className="scene-detail-empty-subtitle">点击表格中场次名称右侧的"进入"按钮查看详情</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>



        {/* ElementSearch 弹窗 */}
        {elementSearchVisible && breakdownId && (
          <ElementSearch
            value={null}
            onChange={handleElementSearchChange}
            visible={elementSearchVisible}
            onClose={handleElementSearchClose}
            breakdownID={breakdownId}
            currentElementType={elementSearchType}
            sceneID={selectedSceneId || undefined}
            selectedElementIds={Array.from(selectedElementIds)}
          />
        )}

        {/* 查找替换弹窗已在 BreakdownTable 中处理 */}

        {/* 确认对话框 */}
        <ConfirmDialog
          visible={dialogState.visible}
          message={dialogState.message}
          title={dialogState.title}
          confirmText={dialogState.confirmText}
          cancelText={dialogState.cancelText}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />

      </div>
    );
  };
  
export default BreakdownMain;
